import classNames from "classnames";
import { Col, Container, Row } from "react-grid-system";
import AddAutopay from "./AddAutopay";
import {
  EnrollmentType,
  GetAccountDetailsResponse,
  GetAccountResponse,
  GetAutopayEnrollmentResponse,
} from "../../lib/api/models";
import UpdateAutopay from "./UpdateAutopay";
import { useGetAutopayEnrollment } from "../../lib/api/queries";
import StyledButton from "../common/buttons/StyledButton";
import StyledError from "../common/StyledError";
import Spinner from "../common/Spinner";
import CreateAutopayIneligible from "./UpdateAutopay/CreateAutopayIneligible";
import { ToolState } from "../AccountDetails";
import { useCallback, useEffect } from "react";

import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./ManageAutopay.module.css";
import { UseQueryResult } from "@tanstack/react-query";

const strings = {
  addAutopay: "Add Autopay",
  manageAutopay: "Manage Autopay",
  close: "Close",
};

type Props = {
  tool: ToolState;
  account: GetAccountResponse;
  accountDetailsQueryResult: UseQueryResult<GetAccountDetailsResponse>;
  onRequestClose: () => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  onComplete: (summaryWithoutTime: string) => void;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  className?: string;
  referenceId?: string;
};

const getTitle = (
  autopayEnrollmentResponse?: GetAutopayEnrollmentResponse,
): string => {
  if (
    autopayEnrollmentResponse &&
    autopayEnrollmentResponse.enrollmentType === EnrollmentType.Eligible &&
    !autopayEnrollmentResponse.isEnrolledAutopay
  ) {
    return strings.addAutopay;
  }
  return strings.manageAutopay;
};

export default function ManageAutopay({
  tool,
  account,
  accountDetailsQueryResult: {
    data: accountDetails,
    isLoading: isGetAccountDetailsLoading,
    isError: isGetAccountDetailsError,
    error: getAccountDetailsError,
    refetch: getAccountDetailsRefetch,
  },
  onRequestClose,
  onDisplayGeneralErrors,
  onComplete,
  onSetToolInProgress,
  className,
  referenceId,
}: Props) {
  // Refetch data on load to ensure freshness
  useEffect(() => {
    getAccountDetailsRefetch();
  }, [getAccountDetailsRefetch]);

  const {
    data: getAutopayEnrollmentResponse,
    isLoading: isGetAutopayEnrollmentResponseLoading,
    isError: isGetAutopayEnrollmentResponseError,
    error: getAutopayEnrollmentResponseError,
    refetch: getAutopayEnrollmentResponseRefetch,
  } = useGetAutopayEnrollment({
    partyId: account.partyId,
    dwbuid: account.currentDwbuid,
  });

  const setToolInProgress = useCallback(
    (b: boolean) => {
      onSetToolInProgress(tool.id, tool.inProgress || b);
    },
    [onSetToolInProgress, tool.id, tool.inProgress],
  );

  const renderAddAutopay = () => {
    return isGetAccountDetailsLoading ? (
      <Row>
        <Col>
          <Spinner className={styles.spinner} />
        </Col>
      </Row>
    ) : isGetAccountDetailsError ? (
      <Row>
        <Col>
          <StyledError
            error={getAccountDetailsError}
            errorTitle={strings.addAutopay}
            refetch={getAccountDetailsRefetch}
          />
        </Col>
      </Row>
    ) : (
      <AddAutopay
        account={account}
        accountDetails={accountDetails}
        onDisplayGeneralErrors={onDisplayGeneralErrors}
        onComplete={onComplete}
        setToolInProgress={setToolInProgress}
        referenceId={referenceId}
      />
    );
  };

  return (
    <div className={classNames(toolStyles.container, className)} id={tool.id}>
      <Container>
        <Row className={toolStyles.header}>
          <Col md={11} className={toolStyles.centerCol}>
            <h2 className={toolStyles.title}>
              {getTitle(getAutopayEnrollmentResponse)}
            </h2>
          </Col>
          <Col md={1} className={toolStyles.centerCol}>
            <StyledButton
              className={toolStyles.headerButton}
              variant="secondary"
              onClick={onRequestClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {isGetAutopayEnrollmentResponseLoading ? (
          <Row>
            <Col>
              <Spinner className={styles.spinner} />
            </Col>
          </Row>
        ) : isGetAutopayEnrollmentResponseError ? (
          <Row>
            <Col>
              <StyledError
                error={getAutopayEnrollmentResponseError}
                errorTitle={strings.manageAutopay}
                refetch={getAutopayEnrollmentResponseRefetch}
              />
            </Col>
          </Row>
        ) : getAutopayEnrollmentResponse.enrollmentType ===
          EnrollmentType.Eligible ? (
          getAutopayEnrollmentResponse.isEnrolledAutopay ? (
            <UpdateAutopay
              account={account}
              autopayInfo={getAutopayEnrollmentResponse}
              onComplete={onComplete}
              onDisplayGeneralErrors={onDisplayGeneralErrors}
              setToolInProgress={setToolInProgress}
              referenceId={referenceId}
            />
          ) : (
            renderAddAutopay()
          )
        ) : (
          <CreateAutopayIneligible
            onClose={onRequestClose}
            ineligibleAutopayEnrollmentResponse={getAutopayEnrollmentResponse}
          />
        )}
      </Container>
    </div>
  );
}
